import React, { useEffect, useState, useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart, CategoryScale, LinearScale, Title, BarElement, Tooltip } from "chart.js";
import { fetchReportInteractions } from "../../../services/httpClient";
import { loadingCard, wait } from "../../../helpers/loading";

Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

interface InteractionsBarChartProps {
  data?: any[];
  startDate?: string;
  endDate?: string;
  useDatePicker?: boolean;
  isActive?: boolean;
}

export const InteractionsBarChart = ({ data, startDate, endDate, useDatePicker = false, isActive = false }: InteractionsBarChartProps) => {
  const [fetchedData, setFetchedData] = useState<any>();
  const chartRef = useRef<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (data) {
        setFetchedData(data);
      } else {
        setFetchedData(null);
        const starting = new Date();
        starting.setDate(starting.getDate() - 365);
        let start = starting.toISOString().slice(0, 10);
        let end = new Date().toISOString().slice(0, 10);

        if (useDatePicker) {
          start = startDate ?? start;
          end = endDate ?? end;
        }

        await getInteractions(start!, end!);
      }
    };

    fetchData();
  }, [data, startDate, endDate]);

  // Trigger a resize when the tab becomes active
  useEffect(() => {
    if (!isActive || !chartRef.current) return;
    const inst = chartRef.current?.chart || chartRef.current;
    if (typeof inst.resize === "function") inst.resize();
  }, [isActive]);

  const getInteractions = async (start: string, end: string) => {
    fetchReportInteractions(start, end)
      .then(async (res: any) => {
        const data = res[0].data;

        const creators = data.creator_counts || [];
        setFetchedData(
          creators.map((c: any) => ({
            created_by: c.created_by,
            creator_firstname: c.firstname,
            creator_lastname: c.lastname,
            count: parseInt(c.count),
          })),
        );
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const chartData = {
    labels:
      fetchedData?.length > 0
        ? fetchedData?.map((item: any) => item.creator_firstname + " " + item.creator_lastname)
        : ["No data available"],
    datasets: [
      {
        label: "Interactions",
        data: fetchedData?.map((item: any) => item.count),
        backgroundColor: ["#5D82A0"],
      },
    ],
  };

  const maxCount = fetchedData?.length ? Math.max(...fetchedData?.map((item: any) => item.count)) : 0;
  const maxCountWithBuffer = Math.ceil(maxCount / 5) * 5;

  const options = {
    indexAxis: "y" as const,
    maintainAspectRatio: false,
    datasets: {
      bar: {
        maxBarThickness: 30,
      },
    },
    scales: {
      x: {
        max: maxCountWithBuffer,
        ticks: {
          font: {
            size: 14,
          },
        },
      },
      y: {
        beginAtZero: true,
        max: maxCount + 50,
        ticks: {
          font: {
            size: 14,
          },
        },
      },
    },
    plugins: {
      title: {
        display: true,
        text: "Case Manager Interactions",
        color: "#5a7082",
        font: {
          size: 18,
          weight: 500,
        },
      },
      legend: {
        display: false,
      },
    },
    elements: {
      bar: {
        borderWidth: 1,
        borderRadius: 4,
        barPercentage: 0.8,
        categoryPercentage: 0.9,
      },
    },
  };

  if (!fetchedData) {
    return loadingCard();
  }

  return (
    <div style={{ height: `${chartData.labels.length * 50}px`, minHeight: "150px" }}>
      {chartData.labels.length > 0 && <Bar ref={chartRef} data={chartData} options={options} />}
    </div>
  );
};
